import { setInteractiveSimulationData } from '@/redux/slices/interactiveSimulationSlice';
import { Button, Input, Modal } from '@hydrodigital/design-system';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import { DatePicker, Form, notification } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AnyAction } from 'redux';
import InteractiveSimulationService from '../../domain/InteractiveSimulations/InteractiveSimulationService';
import LocationService from '../../domain/Location/LocationService';
import useInpFiles from '../../hooks/useInpFiles';

export interface IProps {
  showCreationModal: boolean;
  onCancel: Function;
  onSuccess: Function;
}

const locationService = new LocationService();
const interactiveSimulationService = new InteractiveSimulationService();

export default function ModalCreationInteractiveSimulation({
  showCreationModal,
  onCancel,
  onSuccess,
}: IProps) {
  const security = localStorage.getItem('security') || '{}';
  const organizationId = JSON.parse(security).organizationId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [locations, setLocations] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [selectLocation, setSelectLocation] = useState(0);
  const [selectedInp, setSelectedInp] = useState(0);
  const [startDate, setStartdate] = useState('');
  const [finishDate, setFinishDate] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [stepTime, setStepTime] = useState(0);
  const [selectedLocationValue, setSelectedLocationValue] = useState<string | undefined>(undefined);
  const [selectedInpValue, setSelectedInpValue] = useState<string | undefined>(undefined);

  const { inpFiles } = useInpFiles({
    organizationId,
    locationId: selectedLocationValue ? Number(selectedLocationValue) : null,
    requireLocation: true,
    size: 500,
    onlyCompleted: true,
  });

  const inpOptions: ComboboxOption[] = inpFiles
    .filter((p: any) => p.engineType !== 'ICM')
    .map((p: any) => ({
      value: String(p.inpId),
      label: `${p.inpId} - ${p.inpName}`,
    }));

  function parseLocations(locations: any[]): ComboboxOption[] {
    return locations.map((location) => ({
      value: String(location.locationId),
      label: location.locationName,
      children: location.childLocations
        ? parseLocations(location.childLocations)
        : [],
      disabled: !location.canShowLocation,
    }));
  }

  const handleClickSelectLocation = (value: any) => {
    setSelectLocation(value);
  };

  const handleSaveSimulation = async (values: any) => {
    const res = await interactiveSimulationService.postInteractiveSimulation({
      ...values,
    });
    if (res.success) {
      notification.success({
        message: 'Successful',
        description: 'Interactive Simulation created successfully',
      });
      const simulationId = JSON.parse(
        res.data.replaceAll('\\', ''),
      ).simulationId;
      localStorage.setItem(
        'interactiveSimulationData',
        JSON.stringify({
          ...values,
          simulationId,
        }),
      );
      dispatch(
        setInteractiveSimulationData({ ...values }) as unknown as AnyAction,
      );
      navigate(
        `/interactive-simulation-v2/${values.locationId}/${simulationId}`,
      );
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId') || '';
    const resLocationByUser = locationService.getLocationsByUser(userId);
    resLocationByUser.then((value) => {
      setLocations(value);
    });
  }, []);

  // useEffect(() => {
  //   if (form.getFieldValue("locationId")) {
  //   }
  // }, [form]);

  return (
    <Modal
      isOpen={showCreationModal}
      onClose={() => {
        onCancel();
        form.resetFields();
        setSelectedLocationValue(undefined);
        setSelectedInpValue(undefined);
      }}
      size="s"
      showCloseButton={false}
    >
      <Modal.Header>
        <Modal.Title text="CREATE SIMULATIONS" />
      </Modal.Header>

      <Modal.Content>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            handleSaveSimulation({
              ...values,
              inpId: Number(values.inpId),
            });
          }}
          onFinishFailed={(errorInfo) => {
            console.log('Failed:', errorInfo);
          }}
          autoComplete="off"
          size="small"
          style={{ maxWidth: 700 }}
        >
          <Form.Item
            name="simulationName"
            label="Simulation name:"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
            ]}
          >
            <Input placeholder="" size="small" />
          </Form.Item>
          <Form.Item
            name="locationId"
            label="Select Location:"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
            ]}
          >
            <Combobox
              items={parseLocations(locations)}
              value={selectedLocationValue}
              onValueChange={(val) => {
                setSelectedLocationValue(String(val));
                form.setFieldsValue({
                  locationId: val,
                });
                if (val) {
                  form.setFieldsValue({
                    inpId: null,
                  });
                  setSelectedInpValue(undefined);
                }
              }}
              onClear={() => {
                setSelectedLocationValue(undefined);
                setSelectedInpValue(undefined);
                form.setFieldsValue({
                  locationId: undefined,
                  inpId: undefined,
                });
              }}
              size="small"
            >
              <ComboboxInput placeholder="Select Location" />
              <ComboboxContent>
                <ComboboxEmpty>No locations found.</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Form.Item>
          <Form.Item
            name="inpId"
            label="Select Inp:"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
              {
                validator: (_, value) =>
                  value && value !== '0'
                    ? Promise.resolve()
                    : Promise.reject(new Error('Should select an option')),
              },
            ]}
          >
            <Combobox
              key={`inp-${selectedLocationValue ?? 'none'}`}
              items={inpOptions}
              value={selectedInpValue}
              onValueChange={(val) => {
                setSelectedInpValue(String(val));
                form.setFieldValue('inpId', String(val));
                setSelectedInp(Number(val));
              }}
              onClear={() => {
                setSelectedInpValue(undefined);
                form.setFieldValue('inpId', undefined);
              }}
              disabled={!selectedLocationValue}
              size="small"
            >
              <ComboboxInput placeholder="Select an option" />
              <ComboboxContent>
                <ComboboxEmpty>No records found.</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Form.Item>
          <Form.Item
            name="startDate"
            label="Select Start date:"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
            ]}
          >
            <DatePicker showTime style={{ width: '100%', borderRadius: '20px' }} />
          </Form.Item>
          <Form.Item
            name="endDate"
            label="Select End date:"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
              {
                validator: (_, value) =>
                  value > form.getFieldValue('startDate')
                    ? Promise.resolve()
                    : Promise.reject(new Error('Should be greater than the start date')),
              },
            ]}
          >
            <DatePicker showTime style={{ width: '100%', borderRadius: '20px' }} />
          </Form.Item>
          <Form.Item
            name="stepTime"
            label="Time interval (min):"
            rules={[
              {
                required: true,
                message: <span style={{ display: 'inline-block' }}>Required</span>,
              },
            ]}
          >
            <Input type="number" min={1} max={60} size="small" placeholder="" />
          </Form.Item>
        </Form>
      </Modal.Content>

      <Modal.Footer className="flex justify-between">
        <Button
          label="Accept"
          variant="primary"
          showIcon={false}
          size="small"
          onClick={() => form.submit()}
        />
        <Button
          label="Cancel"
          variant="secondary"
          showIcon={false}
          size="small"
          onClick={() => {
            onCancel();
            form.resetFields();
            setSelectedLocationValue(undefined);
            setSelectedInpValue(undefined);
          }}
        />
      </Modal.Footer>
    </Modal>
  );
}
